import { ObjUtils, Iterable, IComparable } from "../Utils/ObjUtils";
import { InfoSetTable } from "../InfoSets/InfoSetTable";
import { StringUtils } from "../Utils/StringUtils";
import { Device } from "../Core/Device";
import { List } from "../Utils/List";
import { SnmpConstants } from "../Core/SnmpConstants";
import { AssetEntry, AssetTable } from "../InfoSets/AssetTable";
import { SensorEntry, SensorTable } from "../InfoSets/SensorTable";
import { ObjectSerializer } from "../Serialization/ObjectSerializer";
import { ObjSerUtils } from "../Serialization/ObjSerUtils";
import { ValueEncoder } from "../Utils/ValueEncoder";

//export interface EntPhysicalEntryJson {
//    index:string;
//    name: string;
//    description: string;
//    vendor_type: string;
//    contained_in: string;
//    class: string;
//    parent_rel_pos: string;
//    hw_rev: string;
//    fw_rev: string;
//    sw_rev: string;
//    serial_number: string;
//    manufacturer_name: string;
//    model: string;
//    alias: string;
//    asset_id: string;
//    fru: string;
//    unit_index: string;
//    module_index: string;
//    sub_module_index: string;
//    sub_sub_module_index: string;
//}

export enum EntPhysicalClass {
	other = 1,
	unknown,
	chassis,
	backplane,
	container,
	powerSupply,
	fan,
	sensor,
	module,
	port,
	stack,
	cpu
};

export class EntPhysicalCollector {
	public static entPhysicalClass_other = 1;
	public static entPhysicalClass_unknown = 2;
	public static entPhysicalClass_chassis = 3;
	public static entPhysicalClass_backplane = 4;
	public static entPhysicalClass_container = 5;
	public static entPhysicalClass_powerSupply = 6;
	public static entPhysicalClass_fan = 7;
	public static entPhysicalClass_sensor = 8;
	public static entPhysicalClass_module = 9;
	public static entPhysicalClass_port = 10;
	public static entPhysicalClass_stack = 11;
}

export class EntPhysicalEntry /*implements ISerializable*/ implements ObjectSerializer {
	// KEY
	public Index: number;

	// ATTRIBUTES
	public Name: string;
	public get HasName(): boolean {
		return !!this.Name;
	}

	public Description: string;
	public get HasDescription(): boolean {
		return !!this.Description;
	}

	public VendorType: string;
	public get HasVendorType(): boolean {
		return !!this.VendorType;
	}

	public ContainedIn: number;
	public get HasContainedIn(): boolean {
		return this.ContainedIn >= 0;
	}

	public Class: number;
	public get HasClass(): boolean {
		return this.Class >= 0;
	}

	public ParentRelPos: number;
	public get HasParentRelPos(): boolean {
		return this.ParentRelPos >= 0;
	}

	public HwRev: string;
	public get HasHwRev(): boolean {
		return !!this.HwRev;
	}

	public FwRev: string;
	public get HasFwRev(): boolean {
		return !!this.FwRev;
	}

	public SwRev: string;
	public get HasSwRev(): boolean {
		return !!this.SwRev;
	}

	public SerialNum: string;
	public get HasSerialNum(): boolean {
		return !!this.SerialNum;
	}

	public MfgName: string;
	public get HasMfgName(): boolean {
		return !!this.MfgName;
	}

	public ModelName: string;
	public get HasModelName(): boolean {
		return !!this.ModelName;
	}

	public Alias: string;
	public get HasAlias(): boolean {
		return !!this.Alias;
	}

	public AssetID: string;
	public get HasAssetID(): boolean {
		return !!this.AssetID;
	}

	public IsFRU: boolean;

	public UnitIndex: number;
	public get HasUnitIndex(): boolean {
		return this.UnitIndex >= 0;
	}

	public ModuleIndex: number;
	public get HasModuleIndex(): boolean {
		return this.ModuleIndex >= 0;
	}

	public SubModuleIndex: number;
	public get HasSubModuleIndex(): boolean {
		return this.SubModuleIndex >= 0;
	}

	public SubSubModuleIndex: number;
	public get HasSubSubModuleIndex(): boolean {
		return this.SubSubModuleIndex >= 0;
	}

	constructor(entIndex?: number) {
		this.Index = entIndex;
		this.Init();
	}

	private Init(): void {
		this.VendorType = '';
		this.ContainedIn = -1;
		this.Class = -1;
		this.ParentRelPos = -1;
		this.Name = '';
		this.HwRev = '';
		this.FwRev = '';
		this.SwRev = '';
		this.SerialNum = '';
		this.MfgName = '';
		this.ModelName = '';
		this.Alias = '';
		this.AssetID = '';
		this.IsFRU = false;

		this.ClearContainment();
	}

	public ClearContainment(): void {
		this.UnitIndex = -1;
		this.ModuleIndex = -1;
		this.SubModuleIndex = -1;
		this.SubSubModuleIndex = -1;
	}

	public get HasAssetInfo(): boolean {
		if (!this.HasSerialNum)
			return false;
		if (!this.HasModelName)
			return false;
		if (!this.HasHwRev)
			return false;
		if (!this.HasSwRev)
			return false;
		if (!this.HasFwRev)
			return false;

		return true;
	}

	public get IsPort(): boolean {
		return this.Class === EntPhysicalClass.port;
	}

	public get IsValidContainer(): boolean {
		if (
			(this.Class === EntPhysicalCollector.entPhysicalClass_chassis) ||
			(this.Class === EntPhysicalCollector.entPhysicalClass_stack) ||
			(this.Class === EntPhysicalCollector.entPhysicalClass_module) ||
			(this.Class === EntPhysicalCollector.entPhysicalClass_container)) {
			return true;
		}

		return false;
	}

	public ContainmentToString(): string {
		var containmentStr = '';

		containmentStr = StringUtils.Format("{0}/{1}/{2}/{3}", this.UnitIndex, this.ModuleIndex, this.SubModuleIndex, this.SubSubModuleIndex);

		return (containmentStr);
	}

	public Equals(obj: any): boolean {
		if (!obj)
		    return false;

		if (obj === this)
			return true;

		if (!(obj instanceof EntPhysicalEntry))
		    return false;

		var other = <EntPhysicalEntry> obj;

		return other.Index === this.Index;
	}

	public GetHashCode(): number {
		return ObjUtils.GetHashCode(this.Index);
    }

    //public Deserialize(json: EntPhysicalEntryJson): void {
    //    if (json.index !== undefined)
    //        this.Index = parseInt(json.index);
    //    if (json.name !== undefined)
    //        this.Name = json.name;
    //    if (json.description !== undefined)
    //        this.Description = json.description;
    //    if (json.vendor_type !== undefined)
    //        this.VendorType = json.vendor_type;
    //    if (json.contained_in !== undefined)
    //        this.ContainedIn = parseInt(json.contained_in);
    //    if (json.class !== undefined)
    //        this.Class = parseInt(json.class);
    //    if (json.parent_rel_pos !== undefined)
    //        this.ParentRelPos = parseInt(json.parent_rel_pos);
    //    if (json.hw_rev !== undefined)
    //        this.HwRev = json.hw_rev;
    //    if (json.fw_rev !== undefined)
    //        this.FwRev = json.fw_rev;
    //    if (json.sw_rev !== undefined)
    //        this.SwRev = json.sw_rev;
    //    if (json.serial_number !== undefined)
    //        this.SerialNum = json.serial_number;
    //    if (json.manufacturer_name !== undefined)
    //        this.MfgName = json.manufacturer_name;
    //    if (json.model !== undefined)
    //        this.ModelName = json.model;
    //    if (json.alias !== undefined)
    //        this.Alias = json.alias;
    //    if (json.asset_id !== undefined)
    //        this.AssetID = json.asset_id;
    //    if (json.fru !== undefined)
    //        this.IsFRU = StringUtils.ParseBool(json.fru);
    //    if (json.unit_index !== undefined)
    //        this.UnitIndex = parseInt(json.unit_index);
    //    if (json.module_index !== undefined)
    //        this.ModuleIndex = parseInt(json.module_index);
    //    if (json.sub_module_index !== undefined)
    //        this.SubModuleIndex = parseInt(json.sub_module_index);
    //    if (json.sub_sub_module_index !== undefined)
    //        this.SubSubModuleIndex = parseInt(json.sub_sub_module_index);
    //}

	public Write(obj: any, context: any): void {
		obj.ent_physical_entry = {};
		obj = obj.ent_physical_entry;

		obj.index = ValueEncoder.EncodeInteger(this.Index);

		if (this.HasName)
			obj.name = this.Name;

		if (this.HasDescription)
			obj.description = this.Description;

		if (this.HasVendorType)
			obj.vendor_type = this.VendorType;

		if (this.HasContainedIn)
			obj.contained_in = ValueEncoder.EncodeInteger(this.ContainedIn);

		obj.class = ValueEncoder.EncodeInteger(this.Class);

		if (this.HasParentRelPos)
			obj.parent_rel_pos = ValueEncoder.EncodeInteger(this.ParentRelPos);

		if (this.HasHwRev)
			obj.hw_rev = this.HwRev;

		if (this.HasFwRev)
			obj.fw_rev = this.FwRev;

		if (this.HasSwRev)
			obj.sw_rev = this.SwRev;

		if (this.HasSerialNum)
			obj.serial_number = this.SerialNum;

		if (this.HasMfgName)
			obj.manufacturer_name = this.MfgName;

		if (this.HasModelName)
			obj.model = this.ModelName;

		if (this.HasAlias)
			obj.alias = this.Alias;

		if (this.HasAssetID)
			obj.asset_id = this.AssetID;

		obj.fru = ValueEncoder.EncodeBoolean(this.IsFRU);

		if (this.HasUnitIndex)
			obj.unit_index = ValueEncoder.EncodeInteger(this.UnitIndex);

		if (this.HasModuleIndex)
			obj.module_index = ValueEncoder.EncodeInteger(this.ModuleIndex);

		if (this.HasSubModuleIndex)
			obj.sub_module_index = ValueEncoder.EncodeInteger(this.SubModuleIndex);

		if (this.HasSubSubModuleIndex)
			obj.sub_sub_module_index = ValueEncoder.EncodeInteger(this.SubSubModuleIndex);
	}

	public Read(obj: any, context: any): void {
		if (!obj.ent_physical_entry)
			throw new Error();
		obj = obj.ent_physical_entry;

		if (obj.index !== undefined)
			this.Index = ValueEncoder.DecodeInteger(obj.index);

		if (obj.name !== undefined)
			this.Name = obj.name;

		if (obj.description !== undefined)
			this.Description = obj.description;

		if (obj.vendor_type !== undefined)
			this.VendorType = obj.vendor_type;

		if (obj.contained_in !== undefined)
			this.ContainedIn = ValueEncoder.DecodeInteger(obj.contained_in);

		if (obj.class !== undefined)
			this.Class = ValueEncoder.DecodeInteger(obj.class);

		if (obj.parent_rel_pos !== undefined)
			this.ParentRelPos = ValueEncoder.DecodeInteger(obj.parent_rel_pos);

		if (obj.hw_rev !== undefined)
			this.HwRev = obj.hw_rev;

		if (obj.fw_rev !== undefined)
			this.FwRev = obj.fw_rev;

		if (obj.sw_rev !== undefined)
			this.SwRev = obj.sw_rev;

		if (obj.serial_number !== undefined)
			this.SerialNum = obj.serial_number;

		if (obj.manufacturer_name !== undefined)
			this.MfgName = obj.manufacturer_name;

		if (obj.model !== undefined)
			this.ModelName = obj.model;

		if (obj.alias !== undefined)
			this.Alias = obj.alias;

		if (obj.asset_id !== undefined)
			this.AssetID = obj.asset_id;

		if (obj.fru !== undefined)
			this.IsFRU = ValueEncoder.DecodeBoolean(obj.fru);

		if (obj.unit_index !== undefined)
			this.UnitIndex = ValueEncoder.DecodeInteger(obj.unit_index);

		if (obj.module_index !== undefined)
			this.ModuleIndex = ValueEncoder.DecodeInteger(obj.module_index);

		if (obj.sub_module_index !== undefined)
			this.SubModuleIndex = ValueEncoder.DecodeInteger(obj.sub_module_index);

		if (obj.sub_sub_module_index !== undefined)
			this.SubSubModuleIndex = ValueEncoder.DecodeInteger(obj.sub_sub_module_index);
	}
}

export class EntityAssetInfo {
	constructor(
		public serialNum: string = '',
		public modelName: string = '',
		public hwRev: string = '',
		public swRev: string = '',
		public fwRev: string = '',
		public found: boolean = false) {

	}
}

export class EntPhysicalTable extends InfoSetTable<number, EntPhysicalEntry>
{
	constructor(device: Device) {
		super(device);
	}

	private BuildContainerContainment(entEntry: EntPhysicalEntry): void {
		if (entEntry == null)
			return;
		if (entEntry.Class != EntPhysicalCollector.entPhysicalClass_container)
			return;

		var parentEntEntry: EntPhysicalEntry = null;

		if (entEntry.ContainedIn > 0) {
			parentEntEntry = this.Find(entEntry.ContainedIn);
			if (parentEntEntry != null) {
				if (parentEntEntry.IsValidContainer) {
					entEntry.UnitIndex = parentEntEntry.UnitIndex;

					// CONTAINER within a CONTAINER
					if (parentEntEntry.Class == EntPhysicalCollector.entPhysicalClass_container) {
						// COPY the complete containment
						entEntry.UnitIndex = parentEntEntry.UnitIndex;
						entEntry.ModuleIndex = parentEntEntry.ModuleIndex;
						entEntry.SubModuleIndex = parentEntEntry.SubModuleIndex;
						entEntry.SubSubModuleIndex = parentEntEntry.SubSubModuleIndex;
					}
					else if (parentEntEntry.Class == EntPhysicalCollector.entPhysicalClass_module) {
						// CHECK for PARENT ModuleIndex ALREADY set
						if (parentEntEntry.ModuleIndex >= 0) {
							// COPY ModuleIndex
							entEntry.ModuleIndex = parentEntEntry.ModuleIndex;

							// CHECK for SubModuleIndex ALREADY set
							if (parentEntEntry.SubModuleIndex >= 0) {
								// COPY SubModuleIndex
								entEntry.SubModuleIndex = parentEntEntry.SubModuleIndex;

								// CHECK for SubSubModuleIndex ALREADY set
								if (parentEntEntry.SubSubModuleIndex >= 0) {
									// COPY SubSubModuleIndex
									entEntry.SubSubModuleIndex = parentEntEntry.SubSubModuleIndex;
								}
								else {
									// USE PARENT RELATIVE POSITION
									entEntry.SubSubModuleIndex = entEntry.ParentRelPos;
								}
							}
							else {
								// USE PARENT RELATIVE POSITION
								entEntry.SubModuleIndex = entEntry.ParentRelPos;
							}
						}
						else {
							// USE PARENT RELATIVE POSITION
							entEntry.ModuleIndex = entEntry.ParentRelPos;
						}
					}
				}
			}
		}
	}

	private BuildModuleContainment(entEntry: EntPhysicalEntry): void {

		if (entEntry == null)
			return;
		if (entEntry.Class != EntPhysicalCollector.entPhysicalClass_module)
			return;

		var parentEntEntry: EntPhysicalEntry = null;

		if (entEntry.ContainedIn > 0) {
			parentEntEntry = this.Find(entEntry.ContainedIn);
			if (parentEntEntry != null) {
				if (parentEntEntry.IsValidContainer) {
					entEntry.UnitIndex = parentEntEntry.UnitIndex;
					entEntry.ModuleIndex = parentEntEntry.ParentRelPos;

					if (parentEntEntry.Class == EntPhysicalCollector.entPhysicalClass_container) {
						// PARENT is a CONTAINER (check for Sub modules)
						if (parentEntEntry.SubSubModuleIndex >= 0) {
							entEntry.SubModuleIndex = entEntry.SubModuleIndex;
							entEntry.SubSubModuleIndex = entEntry.ParentRelPos;
						}
						else if (parentEntEntry.SubModuleIndex >= 0) {
							entEntry.SubModuleIndex = entEntry.ParentRelPos;
						}
						else {
							// CHECK to see if ModuleIndex is already SET
							if (entEntry.ModuleIndex < 0)
								entEntry.ModuleIndex = entEntry.ParentRelPos;
						}
					}
				}
			}
		}
	}

	public ClearContainment(): void {
		this._table.ForEach((pair) => {
			pair.Value.ClearContainment();
		});
	}

	public RebuildContainment(): void {
		this.ClearContainment();

		var parentEntEntry: EntPhysicalEntry = null;

		this._table.ForEach((pair) => {
			var entEntry = pair.Value;
			// SCREEN the ENTRIES that we WANT to process
			if ((entEntry.Class == EntPhysicalCollector.entPhysicalClass_port) ||
				(entEntry.Class == EntPhysicalCollector.entPhysicalClass_other) ||
				(entEntry.Class == EntPhysicalCollector.entPhysicalClass_unknown)) {
				return;
			}

			switch (entEntry.Class) {
				case EntPhysicalCollector.entPhysicalClass_chassis:
					{
						// IF the CHASSIS object is "CONTAINED"; SET the UNIT to the PARENT RELATIVE POS
						if (entEntry.ContainedIn > 0)
							entEntry.UnitIndex = entEntry.ParentRelPos;
					}
					break;

				case EntPhysicalCollector.entPhysicalClass_stack:
					{
						// IF the STACK object is "CONTAINED" or has RELATIVE position; SET the UNIT to the PARENT RELATIVE POS
						if ((entEntry.ContainedIn > 0) || (entEntry.ParentRelPos >= 0))
							entEntry.UnitIndex = entEntry.ParentRelPos;
					}
					break;

				case EntPhysicalCollector.entPhysicalClass_backplane:
				case EntPhysicalCollector.entPhysicalClass_fan:
				case EntPhysicalCollector.entPhysicalClass_powerSupply:
				case EntPhysicalCollector.entPhysicalClass_sensor:
					{
						// LOOKUP the CONTAINER (get UNIT containment)
						if (entEntry.ContainedIn > 0) {
							parentEntEntry = this.Find(entEntry.ContainedIn);
							if (parentEntEntry != null) {
								entEntry.UnitIndex = parentEntEntry.UnitIndex;
							}
						}
					}
					break;

				case EntPhysicalCollector.entPhysicalClass_module:
					{
						this.BuildModuleContainment(entEntry);
					}
					break;

				case EntPhysicalCollector.entPhysicalClass_container:
					{
						this.BuildContainerContainment(entEntry);
					}
					break;
			}
		});
	}

	public GetEntriesByClass(entPhysicalClass: number): List<EntPhysicalEntry> {
		var classList = new List<EntPhysicalEntry>();

		this._table.ForEach((pair) => {
			if (pair.Value.Class != entPhysicalClass)
				return;

			classList.Add(pair.Value);
		});

		return (classList);
	}

	public GetEntriesContainedIn(containedInIndex: number): List<EntPhysicalEntry> {
		var entryList = new List<EntPhysicalEntry>();

		this._table.ForEach((pair) => {
			if (pair.Value.ContainedIn != containedInIndex)
				return;

			entryList.Add(pair.Value);
		});

		return (entryList);
	}

	public Add(entIndex: number): EntPhysicalEntry {
		var entEntry: EntPhysicalEntry = this._table.Item(entIndex);

		if (!entEntry) {
			entEntry = new EntPhysicalEntry(entIndex);
			this._table.Add(entIndex, entEntry);
		}

		return entEntry;
	}

	public AddEntry(entEntry: EntPhysicalEntry): void {
		this._table.Add(entEntry.Index, entEntry);
	}

	public Clear(): void {
		this._table.Clear();
	}

	public get Count(): number {
		return this._table.Count;
	}

	public Find(entIndex: number): EntPhysicalEntry {
		return this._table.Item(entIndex);
	}

	public FindChassis(switchIndex: number = -1): EntPhysicalEntry {
		var chassisEntry: EntPhysicalEntry = null;

		this._table.Some((pair) => {
			if (pair.Value.Class != SnmpConstants.entPhysicalClass_chassis)
				return;


			if (switchIndex >= 0) {
				if (pair.Value.ParentRelPos != switchIndex)
					return;
			}

			chassisEntry = pair.Value;
			return true;
		});

		return (chassisEntry);
	}

	public FindStandaloneModule(): EntPhysicalEntry {
		var moduleEntry = null;

		this._table.Some((pair) => {
			if (pair.Value.Class != SnmpConstants.entPhysicalClass_module)
				return;

			if (moduleEntry == null)
				moduleEntry = pair.Value;
			else {
				// LOOKING for a UNIQUE MODULE entry
				moduleEntry = null;
				return true;
			}
		});

		return (moduleEntry);
	}

	public RemoveByIndex(entIndex: number): boolean {
		return this._table.Remove(entIndex);
	}
        
	public UpdateAsset_Chassis(): void {
		var entChassisEntry: EntPhysicalEntry = this.FindChassis();
		if (!entChassisEntry)
			entChassisEntry = this.FindStandaloneModule();

		if (!entChassisEntry)
			return;

		var assetTable: AssetTable = this.Device.AddAssetTable();
		var chassisEntry: AssetEntry = assetTable.AddChassis();
		if (chassisEntry != null) {
			chassisEntry.EntPhysicalIndex = entChassisEntry.Index;
			if (entChassisEntry.HasName)
				chassisEntry.Name = entChassisEntry.Name;
			if (entChassisEntry.HasDescription)
				chassisEntry.Description = entChassisEntry.Description;
			if (entChassisEntry.HasModelName)
				chassisEntry.ModelName = entChassisEntry.ModelName;
			if (entChassisEntry.HasSerialNum)
				chassisEntry.SerialNum = entChassisEntry.SerialNum;
			if (entChassisEntry.HasHwRev)
				chassisEntry.HwRev = entChassisEntry.HwRev;
			if (entChassisEntry.HasFwRev)
				chassisEntry.FwRev = entChassisEntry.FwRev;
			if (entChassisEntry.HasSwRev)
				chassisEntry.SwRev = entChassisEntry.SwRev;
			if (entChassisEntry.HasMfgName)
				chassisEntry.MfgName = entChassisEntry.MfgName;
		}
	}

	private GetEntityAssetInfo(parentIndex: number):EntityAssetInfo {
		var e = new EntityAssetInfo();
		
		e.found = this.GetEntriesContainedIn(parentIndex).Some((entry) => {
			if (entry.HasAssetInfo) {
				e.serialNum = entry.SerialNum;
				e.modelName = entry.ModelName;
				e.hwRev = entry.HwRev;
				e.swRev = entry.SwRev;
				e.fwRev = entry.FwRev;
				return true;
			}
			else {
				if (entry.IsValidContainer) {
					e = this.GetEntityAssetInfo(entry.Index);
					if (e.found)
						return true;
				}
			}
		});

		return e;
	}

	public RebuildChassisInfo(): void {
		var e: EntityAssetInfo = null;

		this.GetEntriesByClass(SnmpConstants.entPhysicalClass_chassis).ForEach((chassisEntry) => {
			if (!chassisEntry.HasAssetInfo) {
				var e = this.GetEntityAssetInfo(chassisEntry.Index);
				{
					if (!!e.serialNum)
						chassisEntry.SerialNum = e.serialNum;

					if (!!e.modelName)
						chassisEntry.ModelName = e.modelName;

					if (!!e.hwRev)
						chassisEntry.HwRev = e.hwRev;

					if (!!e.swRev)
						chassisEntry.SwRev = e.swRev;

					if (!!e.fwRev)
						chassisEntry.FwRev = e.fwRev;
				}
			}
		});
	}

	public GetSensorNameDescription(entIndex: number, sensorEntry: SensorEntry): boolean {

		var entEntry: EntPhysicalEntry = this.Find(entIndex);
		if (entEntry != null) {
			sensorEntry.Name = '';
			if (entEntry.HasName)
				sensorEntry.Name = entEntry.Name + '';

			sensorEntry.Description = '';
			if (entEntry.HasDescription)
				sensorEntry.Description = entEntry.Description + '';
		}

		return (entEntry != null);
    }

    //public DeserializeEntry(json: EntPhysicalEntryJson): void {
    //    var entry = new EntPhysicalEntry(parseInt(json.index));
    //    entry.Deserialize(json);
    //    this.AddEntry(entry);
    //}

    //public EntryName(): string {
    //    return "ent_physical_entry";
    //};

    //public static FromJson(json: any, device: Device): EntPhysicalTable {
    //    var result = new EntPhysicalTable(device);
    //    result.Deserialize(json);
    //    return result;
    //}

	public Write(obj: any, context: any): void {
		obj.entity_info = {};
		obj = obj.entity_info;

		obj.entries = [];
		ObjSerUtils.WriteObjSerCollection(obj.entries, this._table.Values(), context);
	}

	public Read(obj: any, context: any): void {
		if (!obj.entity_info)
			throw new Error();
		obj = obj.entity_info;

		this.Clear();

		if (obj.entries !== undefined) {
			obj.entries.forEach((value: any) => {
				let entry = new EntPhysicalEntry();
				entry.Read(value, context);
				this.AddEntry(entry);
			}, this);
		}
	}
}
